import useApiRequest from 'api/useApiRequest';
import { queryCrmContacts, previewSavedCrmGroup } from 'api/services/crmContactService';

// ponytail: caps how many of the missing-email contacts we fetch details for (to list in the popup's
// tooltip) — the count itself (totalHits) is always exact regardless of this cap. Bump if a team
// genuinely has more than this many contacts missing an email in one campaign.
const MISSING_EMAIL_LIST_PAGE_SIZE = 500;

const mapHitsToPeople = (hits) =>
  (hits ?? []).map((hit) => ({ id: hit.id, title: hit.title, contactId: hit.contactId }));

const useCrmContacts = () => {
  const { request } = useApiRequest();

  // 'all' (query-based) selection has no client-side list of ids to check for a missing email, so
  // this re-runs the same contact query with an extra "email not_exists" filter — cheap (a single
  // query) and lets us warn *before* creating anything, same as a bounded selection. Returns null
  // (rather than a count) if the request fails, so a genuine "0 missing" and "couldn't check" stay
  // distinguishable to the caller — the error toast itself is already shown by the API client.
  const countContactsMissingEmail = async ({ filters, query, signal }) => {
    try {
      const { data } = await queryCrmContacts(request, {
        filters: [...(filters ?? []), { field: 'email', operator: 'not_exists' }],
        query: query ?? '',
        pageNumber: 1,
        pageSize: MISSING_EMAIL_LIST_PAGE_SIZE,
        signal,
      });
      return { count: data?.totalHits ?? 0, people: mapHitsToPeople(data?.hits) };
    } catch (error) {
      return null;
    }
  };

  // Same idea, for a group's "select all". Deliberately ignores any active search box text:
  // refresh-contacts-from-group (the actual attach step) always re-syncs the whole group and has no
  // query param of its own, so scoping this count to the search would make the popup's number
  // disagree with what Continue actually attaches.
  const countGroupMembersMissingEmail = async ({ groupId, signal }) => {
    try {
      const { data } = await previewSavedCrmGroup(request, {
        id: groupId,
        filters: [{ field: 'email', operator: 'not_exists' }],
        pageNumber: 1,
        pageSize: MISSING_EMAIL_LIST_PAGE_SIZE,
        signal,
      });
      return { count: data?.totalHits ?? 0, people: mapHitsToPeople(data?.hits) };
    } catch (error) {
      return null;
    }
  };

  return { countContactsMissingEmail, countGroupMembersMissingEmail };
};

export default useCrmContacts;
